import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, SparklesIcon, ShieldCheckIcon, DocumentTextIcon } from '@heroicons/react/20/solid';

export default function HeroSection({ user }) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 text-white">
      {/* Simplified Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Single Gradient Overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.2 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 blur-3xl"
        />

        {/* Subtle Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px'
          }}
        />

        {/* Single Floating Orb */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: [0, 0.3, 0.2],
            x: [0, 50, -20],
            y: [0, -30, 20]
          }}
          transition={{ 
            repeat: Infinity, 
            duration: 15, 
            ease: "easeInOut"
          }}
          className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 mix-blend-screen blur-2xl"
        />
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24 lg:py-32 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
          
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 text-center lg:text-left space-y-8"
          >
            {/* Simple Badge */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center bg-blue-900/40 backdrop-blur-sm px-4 py-2 rounded-full border border-blue-400/30"
            >
              <SparklesIcon className="h-4 w-4 mr-2 text-blue-300" />
              <span className="text-sm font-semibold text-blue-100">AI-Powered Legal Technology</span>
            </motion.div>

            {/* Clean Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight leading-tight"
            >
              <span className="text-white block mb-2">Fight Unfair</span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400 block">
                Parking Tickets
              </span>
            </motion.h1>

            {/* Clear Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-xl md:text-2xl text-blue-100 max-w-2xl mx-auto lg:mx-0 leading-relaxed"
            >
              Generate professional dispute letters in minutes using AI trained on legal precedents. 
              <span className="text-cyan-300 font-semibold"> Fast, effective, and legally sound.</span>
            </motion.p>

            {/* Simple Benefits */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex flex-wrap gap-4 justify-center lg:justify-start"
            >
              <div className="flex items-center text-emerald-300">
                <ShieldCheckIcon className="h-5 w-5 mr-2" />
                <span className="font-medium">Legal Grade</span>
              </div>
              <div className="flex items-center text-blue-300">
                <SparklesIcon className="h-5 w-5 mr-2" />
                <span className="font-medium">AI Powered</span>
              </div>
              <div className="flex items-center text-purple-300">
                <DocumentTextIcon className="h-5 w-5 mr-2" />
                <span className="font-medium">Instant Results</span>
              </div>
            </motion.div>

            {/* Clear Call-to-Action */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.0 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4"
            >
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link
                  to={user ? '/form' : '/login'}
                  className="inline-flex items-center justify-center bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-cyan-500/25 transition-all duration-300 cursor-pointer"
                >
                  <span className="mr-2">
                    {user ? 'Start My Dispute' : 'Get Started Free'}
                  </span>
                  <ChevronRightIcon className="h-5 w-5" />
                </Link>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <a
                  href="#how-it-works"
                  className="inline-flex items-center justify-center bg-slate-800/60 hover:bg-slate-700/80 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 border border-slate-600/30 hover:border-slate-500/50 cursor-pointer"
                >
                  How It Works
                </a>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Right Content - Simplified Document Preview */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="lg:col-span-5 relative hidden lg:block"
          >
            <div className="relative">
              {/* Subtle glow */}
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl blur-xl opacity-20"></div>
              
              {/* Document card */}
              <div className="relative bg-slate-800/90 backdrop-blur-xl p-8 rounded-2xl border border-slate-600/40 shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <img
                      src="https://supabase.zapt.ai/storage/v1/render/image/public/icons/c7bd5333-787f-461f-ae9b-22acbc0ed4b0/55145115-0624-472f-96b9-d5d88aae355f.png?width=48&height=48"
                      alt="Parking Disputer"
                      className="w-12 h-12 rounded-lg"
                    />
                    <div className="ml-3">
                      <div className="text-white font-bold">Dispute Letter</div>
                      <div className="text-slate-400 text-sm">AI Generated</div>
                    </div>
                  </div>
                  <div className="bg-emerald-500 text-white text-xs px-3 py-1 rounded-full font-semibold">
                    Ready
                  </div>
                </div>

                {/* Content preview */}
                <div className="space-y-4 text-slate-300 text-sm">
                  <div className="border-l-2 border-blue-400 pl-4">
                    <div className="text-blue-300 font-medium mb-1">Opening Statement</div>
                    <div>I am writing to dispute parking charge notice #EC12345 issued on 15/04/2023...</div>
                  </div>
                  <div className="border-l-2 border-cyan-400 pl-4">
                    <div className="text-cyan-300 font-medium mb-1">Legal Argument</div>
                    <div>The penalty was issued incorrectly as signage was inadequate...</div>
                  </div>
                  <div className="border-l-2 border-purple-400 pl-4">
                    <div className="text-purple-300 font-medium mb-1">Legal Reference</div>
                    <div>According to Parking (Code of Practice) Act 2019...</div>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-6 flex justify-between items-center pt-4 border-t border-slate-700">
                  <div className="text-slate-400 text-sm">450 words • Legal references included</div>
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Simple bottom wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 200" className="w-full">
          <path fill="#F9FAFB" d="M0,64L1440,96L1440,200L0,200Z"></path>
        </svg>
      </div>
    </section>
  );
}